import { Controller } from "@hotwired/stimulus";
import { dsConfirm } from "../../utils/ds_confirm";

export default class extends Controller {
  static targets = [
    "deviceItem",
    "modal",
    "modalTitle",
    "deviceId",
    "nameInput",
    "typeInput",
    "statusInput",
    "userInput",
    "nameError",
    "deviceTypeError",
    "statusError",
    "userError",
    "submitBtn",
    "submitText",
  ];
  static values = { schoolId: Number };

  connect() {
    this.isEditing = false;
  }

  showCreateForm() {
    this.isEditing = false;
    this.clearErrors();
    this.resetForm();
    this.modalTitleTarget.textContent = this.t(
      "js.rh.teacher_attendances.devices.modal.create_title",
    );
    this.submitTextTarget.textContent = this.t(
      "js.rh.teacher_attendances.devices.buttons.create",
    );
    this.openModal();
  }

  showEditForm(event) {
    this.isEditing = true;
    this.clearErrors();

    const deviceItem = event.currentTarget.closest(
      "tr[data-device-id], div[data-device-id]",
    );
    const { deviceId, deviceName, deviceType, deviceStatus, deviceUserId } =
      deviceItem.dataset;

    this.deviceIdTarget.value = deviceId;
    this.nameInputTarget.value = deviceName || "";
    this.setSelectValue(this.typeInputTarget, deviceType || "");
    this.setSelectValue(this.statusInputTarget, deviceStatus || "");
    if (this.hasUserInputTarget) {
      this.setSelectValue(this.userInputTarget, deviceUserId || "");
    }

    this.modalTitleTarget.textContent = this.t(
      "js.rh.teacher_attendances.devices.modal.edit_title",
    );
    this.submitTextTarget.textContent = this.t(
      "js.rh.teacher_attendances.devices.buttons.update",
    );
    this.openModal();
  }

  openModal() {
    this.modalTarget.classList.remove("hidden");
    document.body.style.overflow = "hidden";
  }

  closeModal() {
    this.modalTarget.classList.add("hidden");
    document.body.style.overflow = "auto";
  }

  resetForm() {
    this.deviceIdTarget.value = "";
    this.nameInputTarget.value = "";
    this.setSelectValue(this.typeInputTarget, "");
    this.setSelectValue(this.statusInputTarget, "inactive");
    if (this.hasUserInputTarget) {
      this.setSelectValue(this.userInputTarget, "");
    }
  }

  setSelectValue(selectElement, value) {
    selectElement.value = value;
    if (selectElement.slim) {
      selectElement.slim.setSelected(value);
    }
  }

  clearErrors() {
    const errorTargets = [
      "nameError",
      "deviceTypeError",
      "statusError",
      "userError",
    ];
    errorTargets.forEach((target) => {
      if (
        this[`has${target.charAt(0).toUpperCase() + target.slice(1)}Target`]
      ) {
        const el = this[`${target}Target`];
        el.textContent = "";
        el.classList.add("hidden");
      }
    });
    // Reset input border colors
    [
      this.nameInputTarget,
      this.typeInputTarget,
      this.statusInputTarget,
    ].forEach((input) => {
      input.classList.remove("border-red-500");
      input.classList.add("border-gray-300");
    });
    if (this.hasUserInputTarget) {
      this.userInputTarget.classList.remove("border-red-500");
      this.userInputTarget.classList.add("border-gray-300");
    }
  }

  showFieldError(target, input, message) {
    target.textContent = message;
    target.classList.remove("hidden");
    input.classList.remove("border-gray-300");
    input.classList.add("border-red-500");
  }

  async handleFormSubmit(event) {
    event.preventDefault();
    this.clearErrors();

    const form = event.target;
    const formData = new FormData(form);
    const deviceId = this.deviceIdTarget.value;

    const url = this.isEditing
      ? `/schools/rh/teacher_attendance_devices/${deviceId}`
      : "/schools/rh/teacher_attendance_devices";
    const method = this.isEditing ? "PATCH" : "POST";

    this.submitBtnTarget.disabled = true;
    this.submitTextTarget.textContent = this.t(
      "js.rh.teacher_attendances.devices.buttons.submitting",
    );

    try {
      const response = await fetch(url, {
        method: method,
        body: formData,
        headers: {
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
          Accept: "application/json",
        },
      });

      const result = await response.json();

      if (result.success) {
        this.showNotification(
          result.message ||
            this.t(
              "js.rh.teacher_attendances.devices.notifications.save_success",
            ),
          "success",
        );
        this.closeModal();
        window.location.reload();
      } else {
        this.displayFieldErrors(result.field_errors || {});
        this.restoreSubmitButton();
      }
    } catch (error) {
      console.error("Erreur:", error);
      this.showNotification(
        this.t("js.rh.teacher_attendances.devices.notifications.generic_error"),
        "error",
      );
      this.restoreSubmitButton();
    }
  }

  displayFieldErrors(fieldErrors) {
    if (!fieldErrors || Object.keys(fieldErrors).length === 0) return;

    const fieldMap = {
      name: { error: this.nameErrorTarget, input: this.nameInputTarget },
      device_type: {
        error: this.deviceTypeErrorTarget,
        input: this.typeInputTarget,
      },
      status: { error: this.statusErrorTarget, input: this.statusInputTarget },
    };
    if (this.hasUserInputTarget && this.hasUserErrorTarget) {
      fieldMap.user = {
        error: this.userErrorTarget,
        input: this.userInputTarget,
      };
      fieldMap.user_id = {
        error: this.userErrorTarget,
        input: this.userInputTarget,
      };
    }

    for (const [field, message] of Object.entries(fieldErrors)) {
      const targets = fieldMap[field];
      if (targets) {
        this.showFieldError(targets.error, targets.input, message);
      }
    }
  }

  restoreSubmitButton() {
    this.submitBtnTarget.disabled = false;
    this.submitTextTarget.textContent = this.isEditing
      ? this.t("js.rh.teacher_attendances.devices.buttons.update")
      : this.t("js.rh.teacher_attendances.devices.buttons.create");
  }

  async syncDevice(event) {
    const deviceItem = event.currentTarget.closest(
      "tr[data-device-id], div[data-device-id]",
    );
    const deviceId = deviceItem.dataset.deviceId;
    let deviceName = deviceItem.dataset.deviceName;
    if (!deviceName) {
      const nameEl =
        deviceItem.querySelector("h4") ||
        deviceItem.querySelector(".text-sm.font-medium.text-gray-900");
      deviceName = nameEl ? nameEl.textContent.trim() : "";
    }

    const button = event.currentTarget;
    const originalText = button.innerHTML;
    button.innerHTML = `<svg class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path></svg>`;
    button.disabled = true;

    try {
      const response = await fetch(
        `/schools/rh/teacher_attendance_devices/${deviceId}/sync`,
        {
          method: "POST",
          headers: {
            "X-CSRF-Token": document.querySelector('[name="csrf-token"]')
              .content,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );
      const result = await response.json();
      if (result.success) {
        this.showNotification(
          this.t(
            "js.rh.teacher_attendances.devices.notifications.sync_success",
            { name: deviceName },
          ),
          "success",
        );
        window.location.reload();
      } else {
        this.showNotification(
          result.message ||
            this.t(
              "js.rh.teacher_attendances.devices.notifications.sync_error",
            ),
          "error",
        );
      }
    } catch (error) {
      console.error("Erreur:", error);
      this.showNotification(
        this.t(
          "js.rh.teacher_attendances.devices.notifications.sync_generic_error",
        ),
        "error",
      );
    } finally {
      button.innerHTML = originalText;
      button.disabled = false;
    }
  }

  async deleteDevice(event) {
    const deviceItem = event.currentTarget.closest(
      "tr[data-device-id], div[data-device-id]",
    );
    const deviceId = deviceItem.dataset.deviceId;
    let deviceName = deviceItem.dataset.deviceName;
    if (!deviceName) {
      const nameEl =
        deviceItem.querySelector("h4") ||
        deviceItem.querySelector(".text-sm.font-medium.text-gray-900");
      deviceName = nameEl ? nameEl.textContent.trim() : "";
    }

    if (
      !(await dsConfirm(
        this.t(
          "js.rh.teacher_attendances.devices.notifications.delete_confirm",
          { name: deviceName },
        ),
        { tone: "danger" },
      ))
    ) {
      return;
    }

    try {
      const response = await fetch(
        `/schools/rh/teacher_attendance_devices/${deviceId}`,
        {
          method: "DELETE",
          headers: {
            "X-CSRF-Token": document.querySelector('[name="csrf-token"]')
              .content,
            Accept: "application/json",
          },
        },
      );
      const result = await response.json();
      if (result.success) {
        this.showNotification(
          result.message ||
            this.t(
              "js.rh.teacher_attendances.devices.notifications.delete_success",
            ),
          "success",
        );
        window.location.reload();
      } else {
        this.showNotification(
          result.message ||
            this.t(
              "js.rh.teacher_attendances.devices.notifications.delete_error",
            ),
          "error",
        );
      }
    } catch (error) {
      console.error("Erreur:", error);
      this.showNotification(
        this.t(
          "js.rh.teacher_attendances.devices.notifications.delete_generic_error",
        ),
        "error",
      );
    }
  }

  t(key, options = {}) {
    if (typeof window !== "undefined" && typeof window.t === "function") {
      return window.t(key, options);
    }
    return key;
  }

  showNotification(message, type) {
    const notification = document.createElement("div");
    notification.className = `fixed top-4 right-4 px-6 py-3 rounded-md shadow-lg z-[60] ${
      type === "success" ? "bg-cta text-white" : "bg-red-500 text-white"
    }`;
    notification.textContent = message;
    document.body.appendChild(notification);
    setTimeout(() => notification.remove(), 3000);
  }
}
